/**
 * Component for the our Photo-Uploads Add-On that can
 * either be enabled permanently or using our Advanced-
 * Rules only e.g. when certain Return Reasons have been
 * selected.
 * 
 * @component
 */

import React from "react"
import { Link } from "react-router-dom"
import { Row,Col,Card,Divider,Button,Form,Upload,message } from "antd"
import { UploadOutlined } from '@ant-design/icons'
import axios from "axios"
import { FormattedMessage } from "react-intl"
import { HOCWithMessageHandler } from "../../components/HOCWithMessageHandler"
import { RETURN_TYPES } from "../../constants/constants";

const FormItem = Form.Item;

class Resolution extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      bucketUrl: undefined,
      submitButtonDisabled: false,
      listImages: {},
      uploadingPhotos: {}, // Track which photos are currently being uploaded
    }
  }
  formRef = React.createRef();

  componentDidMount() {
    // scroll to top of page
    window.scrollTo(0,0)
  }

  handleSubmit = async () => {
    try {
      await this.formRef.current.validateFields()
      const { companyIdentifier, returnNumber } = this.props

      this.setState({
        submitButtonDisabled: true,
      })

      const response = await axios.post('/api/visitors/return/photo/verify', {
        companyIdentifier,
        returnNumber,
      })


      if (response.data.status === "error") {
        this.setState({
          submitButtonDisabled: false
        });
        return this.props.messageApi.error(
          this.getTextUploadPhoto(
            this.props.returnSettings.customPhotoSetting.minimumPhoto,
            this.props.returnSettings.customPhotoSetting.maximumPhoto
          ),
          5
        )
      }

      if (this.props.missingEmailForOrder) {
        return this.props.history.push('/return/info');
      } else if (this.props.returnData.isShippingResolution && this.props.type !== RETURN_TYPES.GIFT_RETURN) {
        return this.props.history.push('/return/address');
      } else {
        return this.props.history.push('/return/method');
      }

    } catch (err) {
      if (err?.errorFields) return // validation error
      this.setState({
        submitButtonDisabled: false,
      })
      return message.error(
        'There was an error communicating with the server. Please try again.',
        4
      )
    }
  };

  // image related functions
  async checkPhoto(file, photoNumber) {
    const allowedFileTypes = [
      "image/png", 
      "image/jpeg", 
      "image/heic", 
      "video/mp4", 
      "video/quicktime", 
      "video/x-m4v"
    ];
    if (!allowedFileTypes.includes(file.type)) {
        message.error("Only jpg, jpeg, png, heic images and mp4, mov, m4v videos smaller than 10MB allowed.", 5);
        return Promise.reject(false);
    }
    if ((file.size / 1024 / 1024) > 10) {
      message.error("Only files smaller than 10 MB allowed.", 5);
      return Promise.reject(false);
    }
    if (this.state.listImages[photoNumber]) {
      message.error("You can only upload one photo per field.",5)
      return Promise.reject(false)
    }
    return true
  }

  async handlePhotoUpload(options,photoNumber) {
    // Mark photo as uploading
    this.setState(prevState => ({
      uploadingPhotos: {
        ...prevState.uploadingPhotos,
        [photoNumber]: true
      }
    }))

    options.onProgress({ percent: 20 })
    let signedUrl = ""
    let bucketUrl = ""
    try {
      let response = await axios.post("/api/visitors/return/photo/sign",{
        fileType: options.file.type,
        fileSize: options.file.size / 1024 / 1024,
        companyIdentifier: this.props.companyIdentifier,
        returnNumber: this.props.returnNumber,
      })
      if (response.data.error === "wrong-size") {
        this.clearUploadingState(photoNumber)
        return message.error("Only files smaller than 10 MB allowed.", 5);
      }
      if (response.data.error === "wrong-format") {
        this.clearUploadingState(photoNumber)
        return message.error("Only jpg, jpeg, png, heic images and mp4, mov, m4v videos smaller than 10MB allowed.", 5);
      }
      signedUrl = response.data.signedRequest
      bucketUrl = response.data.url
      options.onProgress({ percent: 50 })

      this.setState({
        bucketUrl,
        listImages: {
          ...this.state.listImages,
          [photoNumber]: bucketUrl,
        }
      })
    } catch (err) {
      this.clearUploadingState(photoNumber)
      return message.error("Error preparing customer photo upload. Try again.",4)
    }

    try {
      await axios.put(signedUrl, options.file, { headers: { 'Content-Type': options.file.type } });
      options.onProgress({ percent: 75 });
      const request = await axios.post("/api/visitors/return/photo/save", {
        fileType: options.file.type,
        photoUrl: bucketUrl,
        photoNumber: photoNumber,
        companyIdentifier: this.props.companyIdentifier,
        returnNumber: this.props.returnNumber,
      })
      if (request.data.error) {
        this.clearUploadingState(photoNumber)
        return message.error("Error saving customer photo. Try again.",4)
      }
      message.success("Photo uploaded successfully.",5)
      options.onSuccess({ percent: 100 })
      this.clearUploadingState(photoNumber)
      return this.setState({
        ...request.data,
      })
    } catch (err) {
      this.clearUploadingState(photoNumber)
      options.onError({ percent: 0 })
      return message.error("Error saving customer media. Try again.",4)
    }
  }

  // Helper method to clear uploading state
  clearUploadingState = (photoNumber) => {
    this.setState(prevState => {
      const updatedUploadingPhotos = { ...prevState.uploadingPhotos }
      delete updatedUploadingPhotos[photoNumber]
      return { uploadingPhotos: updatedUploadingPhotos }
    })
  }

  async handleRemovePhoto(photoNumber) {
    try {
      // Make a DELETE request to the server
      const request = await axios.post("/api/visitors/return/photo/delete",{
        photoNumber: photoNumber,
        companyIdentifier: this.props.companyIdentifier,
        returnNumber: this.props.returnNumber,
      })
      if (request.data.error) {
        return message.error("Error deleting customer media. Try again.",4)
      }
      return this.setState(prevState => {
        const updatedListImages = { ...prevState.listImages }
        updatedListImages[photoNumber] = null
        return { listImages: updatedListImages }
      })
    } catch (err) {
      return message.error("Error deleting customer media. Try again.",4)
    }
  }
  async handleRemoveAllPhoTo(e) {
    e.preventDefault()

    this.props.history.push('/return/order');
    for (const photoNumber of Object.keys(this.state.listImages)) {
      await axios.post("/api/visitors/return/photo/delete",{
        photoNumber: Number(photoNumber),
        companyIdentifier: this.props.companyIdentifier,
        returnNumber: this.props.returnNumber,
      })
    }
  }
  renderPhotoUpload() {
    const maximumPhoto = this.props?.returnSettings?.customPhotoSetting?.maximumPhoto || 3
    const titlePhotos = this.props?.returnSettings?.customPhotoSetting?.titlePhotos
    const isEnabledTranslation = Boolean(this.props.enableTranslation && this.props.enabledTranslations.find(translation => translation.languageCode === this.props.language))

    const rows = []
    for (let i = 0; i < maximumPhoto; i++) {
      const photoNumber = i + 1
      const isUploading = this.state.uploadingPhotos[photoNumber]
      
      rows.push(
        <Row key={i} type="flex" justify="center" align="middle" style={{ marginTop: 20 }} >
          <Col xs={24} md={10}>
            <Upload
              beforeUpload={(file) => { return this.checkPhoto(file, photoNumber) }}
              customRequest={(options) => { this.handlePhotoUpload(options, photoNumber) }}
              multiple={false}
              showUploadList={{
                showRemoveIcon: !isUploading
              }}
              onRemove={() => {
                this.handleRemovePhoto(photoNumber)
              }}>
              <Button><UploadOutlined />

                {
                  isEnabledTranslation ?
                    <FormattedMessage
                      id={`app.visitor.resolutions.uploadPhotos.photo${photoNumber}`}
                      description="Photo title text"
                      defaultMessage={titlePhotos?.[`title${i + 1}`] || `Photo/Video ${i + 1}`}
                      values={{
                        number: photoNumber
                      }}
                    />
                    :
                    <>{titlePhotos?.[`title${i + 1}`] || `Photo/Video ${i + 1}`}</>
                }

              </Button>
            </Upload>
          </Col>
        </Row>
      )
    }
    return rows
  }

  getTextUploadPhoto = (minimumPhoto,maximumPhoto) => {
    if (minimumPhoto === 0 && maximumPhoto === 1) {
      return <FormattedMessage
      id="app.visitor.resolutions.uploadPhotos.zeroOne"
      description="Text for the upload photos section when the customer can upload one photo"
      defaultMessage="Please upload 1 photo/video"
      values={{
      }}
    />
    }
    if (minimumPhoto === 0 && maximumPhoto > 1) {
      return <FormattedMessage
      id="app.visitor.resolutions.uploadPhotos.zeroMany"
      description="Text for the upload photos section when the customer can upload multiple photos"
      defaultMessage="Please upload up to {maximumPhoto} media"
      values={{
        maximumPhoto
      }}
    />
    }
    if (minimumPhoto === 1 && maximumPhoto > 1) {
      return <FormattedMessage
      id="app.visitor.resolutions.uploadPhotos.oneMany"
      description="Text for the upload photos section when the customer is required to upload at least one photo"
      defaultMessage="Please upload at least {minimumPhoto} photo/video"
      values={{
        minimumPhoto
      }}
    />
    }
    if (minimumPhoto === maximumPhoto) {
      return <FormattedMessage
      id="app.visitor.resolutions.uploadPhotos.equal"
      description="Text for the upload photos section when the customer is required to upload a specific number of photos"
      defaultMessage="Please upload {minimumPhoto} media"
      values={{
        minimumPhoto
      }}
    />
    }
    return <FormattedMessage
      id="app.visitor.resolutions.uploadPhotos.range"
      description="Text for the upload photos section when the customer is required to upload a range of photos"
      defaultMessage="Please upload {minimumPhoto} to {maximumPhoto} media"
      values={{
        minimumPhoto,
        maximumPhoto
      }}
    />
  }
  render() {
    // eslint-disable-next-line no-unused-vars
    const { minimumPhoto,maximumPhoto } = this.props.returnSettings.customPhotoSetting || {}

    return (
      <>
        <Card
          title={
            <FormattedMessage
              id="app.visitor.resolutions.headline"
              description="Title of the third step of the customer portal process ('Additional information')"
              defaultMessage="Upload photos and videos"
              values={{
              }}
            />
          }
          className="textCenter"
        >
          <Form onFinish={this.handleSubmit} ref={this.formRef}>
            <Row type="flex" justify="center" align="middle">
              <Col>
                <img src="/logos/photoAndVideoIcon.svg" alt="Photo and Video" style={{ width: 50, height: 50 }} />
              </Col>
            </Row>
            <Row type="flex" justify="center" align="middle">
              <Col xs={24} md={10}>
                <h4 style={{ display: "flex",justifyContent: "center" }} >{this.getTextUploadPhoto(minimumPhoto,maximumPhoto)}</h4>
                <span>{this.props.returnSettings.customerPhotoUploadDescription}</span>
              </Col>
            </Row>
            {this.renderPhotoUpload()}
            <Divider />
            <Row type="flex" justify="center" align="top">
              <Col>
                <FormItem>
                  <Button>
                    <Link onClick={this.handleRemoveAllPhoTo.bind(this)} to='/return/order'>
                      <FormattedMessage
                        id="app.visitor.order.buttonGoBack"
                        description="Button to go back one screen"
                        defaultMessage="Go back"
                        values={{
                        }}
                      />
                    </Link>
                  </Button>
                  <Divider type="vertical" />
                  <Button type="primary" htmlType="submit" disabled={this.state.submitButtonDisabled} style={{ backgroundColor: this.props.brandColorButton,borderColor: this.props.brandColorButton }}>
                    <span style={{ color: this.props.brandColorButtonText,fontStyle: this.props.brandFontStyle }}>
                      <FormattedMessage
                        id="app.visitor.order.buttonSubmit"
                        description="Button to continue from step 'order' to 'resolution'"
                        defaultMessage="Continue"
                        values={{
                        }}
                      />
                    </span>
                  </Button>
                </FormItem>
              </Col>
            </Row>
          </Form>
        </Card>
      </>
    )
  }
}
export default HOCWithMessageHandler(Resolution)
